import { useRouter } from "@-/com.npmjs.expo-router";
import { showFailureToast } from "@-/dev.improve.haniverse.components.ui.failure-toast";
import { logger } from "@-/dev.improve.haniverse.lib.logger";
import { watchAsyncStep } from "@-/dev.improve.haniverse.lib.async-watchdog";
import { boundedPromise, withDeadline } from "@-/dev.improve.haniverse.lib.deadline";
import { useCallback, useMemo } from "react";
import type { ImperativeRouter } from "@-/com.npmjs.expo-router";
import { useMutation, useQueryClient } from "@-/com.npmjs.tanstack__react-query";
import type { QueryClient } from "@-/com.npmjs.tanstack__react-query";
import { Platform } from "@-/com.npmjs.react-native";

/**
 * Shared by every page navigator's push. Exported so a component that does not own the mutation
 * (or a test driving one) can find an in-flight push in the mutation cache.
 */
export const PAGE_NAVIGATOR_PUSH_MUTATION_KEY = ["pageNavigator", "push"] as const;

type PageNavigatorHref = Exclude<Parameters<ImperativeRouter["push"]>[0], string>;

type QueryClientFetchQueryOptions = Parameters<QueryClient["fetchQuery"]>[0];

// A single TanStack Query options object, identical to one entry of the array
// `useQueries({ queries: [...] })` accepts. Kept structurally loose (only `queryKey`
// is required) so the concrete, invariantly-typed result of `queryOptions(...)`
// assigns without each call site having to widen its generics; `isPageNavigatorQuery`
// validates the shape at runtime before handing it to the query client.
export type PageNavigatorQuery = { [key: string]: unknown; queryKey: readonly unknown[] };

/**
 * A short, stable label for a navigator query, used only for watchdog reporting.
 * Takes the leading string segments of the query key — the same convention the
 * Sentry query-cache instrumentation uses — so a stuck report groups by query
 * kind rather than by the id-bearing tail (which would fragment the grouping).
 */
function describeQueryKey(query: PageNavigatorQuery): string {
  const head: string[] = [];
  for (const segment of query.queryKey) {
    if (typeof segment !== "string") {
      break;
    }
    head.push(segment);
  }
  return head.length > 0 ? head.join(":") : "query";
}

function isPageNavigatorQuery(value: unknown): value is QueryClientFetchQueryOptions {
  if (typeof value !== "object" || value === null) {
    return false;
  }
  if (!("queryKey" in value)) {
    return false;
  }
  return Array.isArray(value.queryKey);
}

export type PageNavigatorOptions = {
  href: PageNavigatorHref;
  /** Render the destination in the current host instead of changing routes. */
  navigate?: (href: PageNavigatorHref) => void;
  /**
   * What the destination reads. Navigation never waits for them: `navigate`/`push`/`replace`
   * commit on the call and warm these alongside the transition, and the destination's Suspense
   * boundaries draw its loading state until they arrive. `prefetch` warms them earlier (press-in,
   * idle), so a warm destination mounts without suspending at all.
   */
  queries: readonly PageNavigatorQuery[];
};

/**
 * The single bound on preparing or warming a set of navigator queries, however many there are.
 *
 * Each query function is already bounded by the query client (30s per attempt), but an awaiter of
 * several could still take several times that, and `fetchQuery` ADOPTS an already-in-flight
 * promise for the same key, so one wedged fetch elsewhere in the app could park it forever. 30s
 * matches one query attempt: the queries run in parallel, so a healthy set is ready as soon as its
 * slowest query answers, and a network read still gets its whole attempt.
 */
export const PAGE_NAVIGATOR_PREPARE_DEADLINE_MS = 30_000;

function describeQueries(queries: readonly PageNavigatorQuery[]): string {
  return queries.length === 0
    ? "(no queries)"
    : queries.map((query) => describeQueryKey(query)).join(", ");
}

/**
 * Strict preparation for a MOUNT that must not suspend: the Learn deck warming a successor card
 * before mounting it under the current one. Navigation never uses it — a route change commits on
 * the press and its destination draws its own loading state.
 *
 * `prefetchQuery` deliberately swallows query errors, so awaiting it is not proof that data exists.
 * `fetchQuery` rejects instead, so a caller only mounts after every query has synchronously
 * readable data.
 *
 * Parallel and bounded as a whole (see {@link PAGE_NAVIGATOR_PREPARE_DEADLINE_MS}). The queries are
 * independent reads — none reads another's cache entry — and `fetchQuery` deduplicates a key that a
 * query function also fetches, so awaiting them one after another only added their latencies.
 */
export async function preparePageNavigatorQueries(
  queryClient: QueryClient,
  queries: readonly PageNavigatorQuery[],
): Promise<void> {
  await withDeadline(
    {
      deadline: PAGE_NAVIGATOR_PREPARE_DEADLINE_MS,
      operation: `navigator: prepare ${describeQueries(queries)}`,
    },
    async () => {
      await Promise.all(
        queries.filter(isPageNavigatorQuery).map((query) =>
          // Still watched per key, so a stuck report names the query that held the mount.
          watchAsyncStep(`navigator: fetchQuery ${describeQueryKey(query)}`, () =>
            queryClient.fetchQuery(query),
          ),
        ),
      );
    },
  );
}

/**
 * Best-effort idle warming. Errors remain in the query cache and never become unhandled.
 *
 * A HOLD, so it fails open: warming is an optimization, and whoever awaits it (an idle prefetch,
 * an entry route's warm-up) proceeds after {@link PAGE_NAVIGATOR_PREPARE_DEADLINE_MS} against
 * whatever the cache holds by then. The warm-ups keep running in the cache; nothing waits on them
 * any longer.
 */
export async function prefetchPageNavigatorQueries(
  queryClient: QueryClient,
  queries: readonly PageNavigatorQuery[],
): Promise<void> {
  await boundedPromise(
    {
      deadline: PAGE_NAVIGATOR_PREPARE_DEADLINE_MS,
      onDeadline: "resolve",
      operation: `navigator: prefetch ${describeQueries(queries)}`,
    },
    (resolve, reject) => {
      void (async () => {
        for (const query of queries) {
          if (!isPageNavigatorQuery(query)) {
            continue;
          }
          await queryClient.prefetchQuery(query);
        }
      })().then(() => resolve(), reject);
    },
  );
}

/**
 * Surface a failed navigation to the user.
 *
 * A navigation no longer waits on data, so the only failure left is the route change itself (an
 * inline host's `navigate`, or the router throwing on a transient navigator state). It must not be
 * silent: a tap that did nothing with no hint why is the worst outcome. The copy is the same
 * classification the page error screen uses.
 */
function reportNavigationFailure(error: unknown): void {
  showFailureToast(error);
}

/**
 * Navigate now and warm the destination's queries alongside the transition. Nothing waits on the
 * warm-ups: the destination's Suspense boundaries draw its loading state until the data arrives,
 * and a failed warm-up stays in the query cache for the destination's own error boundary (which
 * re-fetches on mount, so a transient failure costs nothing).
 */
function navigateAndWarmDestination({
  href,
  navigate,
  queries,
  queryClient,
}: {
  href: PageNavigatorHref;
  navigate: (href: PageNavigatorHref) => void;
  queries: readonly PageNavigatorQuery[];
  queryClient: QueryClient;
}): PageNavigatorHref {
  for (const query of queries) {
    if (isPageNavigatorQuery(query)) {
      void queryClient.prefetchQuery(query);
    }
  }
  navigate(href);
  return href;
}

export function createPageNavigator<TVariables>({
  useNavigator,
}: {
  useNavigator: () => (variables: TVariables) => PageNavigatorOptions;
}) {
  return function usePageNavigator() {
    const router = useRouter();
    const queryClient = useQueryClient();
    const getPageNavigatorOptions = useNavigator();

    // Route changes are local operations: pausing the mutation on reachability would strand even
    // a query-free route.
    const pushMutation = useMutation({
      mutationFn: async (variables: TVariables, { client }) => {
        const { href, navigate, queries } = getPageNavigatorOptions(variables);
        return navigateAndWarmDestination({
          href,
          navigate:
            navigate ??
            ((target) => {
              router.push(target);
            }),
          queries,
          queryClient: client,
        });
      },
      mutationKey: PAGE_NAVIGATOR_PUSH_MUTATION_KEY,
      networkMode: "always",
      onError: reportNavigationFailure,
    });

    const replaceMutation = useMutation({
      mutationFn: async (variables: TVariables, { client }) => {
        const { href, navigate, queries } = getPageNavigatorOptions(variables);
        return navigateAndWarmDestination({
          href,
          navigate:
            navigate ??
            ((target) => {
              router.replace(target);
            }),
          queries,
          queryClient: client,
        });
      },
      mutationKey: ["pageNavigator", "replace"],
      networkMode: "always",
      onError: reportNavigationFailure,
    });

    // Replace WITHOUT warming the destination's queries. For a destination that gates its own
    // reads — post-auth navigation into Learn while the actor database is still opening and
    // pulling — where a warm-up would only run them early against data that does not exist yet.
    // Keeping this on the page navigator preserves typed route ownership without forcing callers to
    // bypass it with a raw router operation.
    const replaceImmediatelyMutation = useMutation({
      mutationFn: async (variables: TVariables) => {
        const { href, navigate } = getPageNavigatorOptions(variables);
        if (navigate) {
          navigate(href);
        } else {
          router.replace(href);
        }
        return href;
      },
      mutationKey: ["pageNavigator", "replaceImmediately"],
      networkMode: "always",
      onError: reportNavigationFailure,
    });

    const navigateMutation = useMutation({
      mutationFn: async (variables: TVariables, { client }) => {
        const { href, navigate, queries } = getPageNavigatorOptions(variables);
        return navigateAndWarmDestination({
          href,
          navigate:
            navigate ??
            ((target) => {
              router.navigate(target);
            }),
          queries,
          queryClient: client,
        });
      },
      mutationKey: ["pageNavigator", "navigate"],
      networkMode: "always",
      onError: reportNavigationFailure,
    });

    // Warm the destination ahead of the navigation (press-in, idle): off-screen-mount the screen
    // (router.prefetch -> react-navigation PRELOAD) AND warm its data queries on web, where
    // preloading is stable. A speed-up only, never a gate: the navigation itself warms the same
    // queries and the destination owns its loading state either way.
    //
    // Native dev builds are much more sensitive to preloaded React Navigation trees while Maestro is
    // rapidly advancing lessons: iOS can surface Metro runtime-global errors from the off-screen
    // preload path, so native warms data only.
    //
    // Android Hermes currently crashes inside React Native's scheduled JS task path when lesson and
    // review screens run background navigator prefetches while Maestro is rapidly advancing cards,
    // so Android skips idle/background warming entirely. Foreground navigation still warms its
    // queries via `navigateAndWarmDestination`.
    const prefetch = useCallback(
      async (variables: TVariables) => {
        const { href, navigate, queries } = getPageNavigatorOptions(variables);
        if (Platform.OS === "android") {
          return href;
        }
        if (navigate || Platform.OS !== "web") {
          await prefetchPageNavigatorQueries(queryClient, queries);
          return href;
        }
        // Never let the off-screen preload take down the app: `router.prefetch` dispatches a
        // react-navigation PRELOAD through live navigator state, and an unlucky moment (navigator
        // mid-transition/teardown) throws — which unmounted the whole tree to a blank page on web
        // (dev-CI run 30616054579, deck-upcoming-inactive-views: "Web ERROR … navigator.ts:236
        // router.prefetch(href)" with the AppLayout component stack, failure screenshot fully
        // blank). Idle preloading is purely an optimization; foreground navigation does not come
        // through here.
        try {
          router.prefetch(href);
        } catch (error) {
          logger(import.meta.location).warn("router.prefetch failed; skipping route preload", {
            error: String(error),
            href,
          });
        }
        await prefetchPageNavigatorQueries(queryClient, queries);
        return href;
      },
      [getPageNavigatorOptions, queryClient, router],
    );

    // Resolve only the destination href. `PageNavigatorRedirect` redirects with it on an entry
    // route's first render; the destination draws its own loading state.
    const resolveHref = useCallback(
      (variables: TVariables) => getPageNavigatorOptions(variables).href,
      [getPageNavigatorOptions],
    );

    // Resolve the destination's query list without navigating. Feed it to
    // `usePrefetchQueries({ queries: navigator.resolveQueries(variables) })` to warm the
    // destination's caches while the current screen is idle, so the eventual navigation
    // mounts without suspending. Memoize the variables at the call site — a fresh array
    // each render re-runs the prefetch effect.
    const resolveQueries = useCallback(
      (variables: TVariables): readonly PageNavigatorQuery[] =>
        getPageNavigatorOptions(variables).queries,
      [getPageNavigatorOptions],
    );

    return useMemo(
      () => ({
        navigate: navigateMutation,
        prefetch,
        push: pushMutation,
        replace: replaceMutation,
        replaceImmediately: replaceImmediatelyMutation,
        resolveHref,
        resolveQueries,
        toJSON() {
          return {};
        },
      }),
      [
        navigateMutation,
        prefetch,
        pushMutation,
        replaceMutation,
        replaceImmediatelyMutation,
        resolveHref,
        resolveQueries,
      ],
    );
  };
}
